import { inject, Injectable } from '@angular/core';
import { RestApiService } from '@core/services/rest-api.service';
import FilterAreaUtils from '@core/utils/filter-area.utils';
import { DvDialogCloseEvent } from '@devinco/ui/dds';
import { TranslateService } from '@ngx-translate/core';
import { FilterCrudDialogService } from '@shared/components/filter-with-selection-crud-dialog/filter-crud-dialog.service';
import { DataFilterAction } from '@shared/constants/data-filter-action';
import { Entity } from '@shared/constants/entity';
import { VtFilterMenuItemType } from '@shared/constants/vt-filter-menu-item-type';
import {
  EntityFilter,
  EntityFilterDto,
  VtFilterMenuItem,
} from '@shared/interfaces/vt-filter-menu-item';
import {
  BehaviorSubject,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

@Injectable()
export class EntityFilterService<T = any> {
  private api = inject(RestApiService);
  private translate = inject(TranslateService);
  private filterCrudDialogService = inject(FilterCrudDialogService);

  filterFields: string[] = [
    'DataFilterID',
    'Editable',
    'Name',
    'FilterModel',
    'VisibleToAllUsers',
  ];

  availableFilters$ = new BehaviorSubject<EntityFilter<T>[]>([]);
  updateFilterDataGetterFn?: () => EntityFilterDto<T> | undefined;
  saveFilterDataGetterFn = (
    responseData: { Name: string; VisibleToAllUsers?: boolean },
    filterCopyData: EntityFilter<T>
  ): Partial<EntityFilterDto<T>> =>
    ({
      Name: responseData.Name,
      FilterModel: filterCopyData.FilterModel,
      VisibleToAllUsers: responseData.VisibleToAllUsers,
      Editable: true,
    }) as Partial<EntityFilterDto<T>>;

  fetchFilters(entity: Entity): void {
    this.api
      .getData({
        entity: entity,
        fields: this.filterFields,
      })
      .pipe(
        map(resp => resp.data),
        map((filters: EntityFilterDto<T>[]) =>
          this.mapFiltersForDvFilterArea(filters, entity)
        ),
        take(1)
      )
      .subscribe(availableFilters =>
        this.availableFilters$.next(availableFilters)
      );
  }

  getFilterMenuItems(
    isEditableFilter: boolean,
    entity: Entity
  ): VtFilterMenuItem[] {
    return [
      {
        title: this.translate.instant('Fleet.Filter.Update'),
        icon: 'save',
        clickFn: () => this.updateFilter(entity),
        itemType: VtFilterMenuItemType.Update,
        disabled: !isEditableFilter,
      },
      {
        title: this.translate.instant('Fleet.Filter.Copy'),
        icon: 'copy',
        clickFn: (dataFilterId: number) =>
          this.copyFilter(dataFilterId, entity),
        itemType: VtFilterMenuItemType.Copy,
      },
      {
        title: this.translate.instant('Fleet.Filter.Delete'),
        icon: 'delete',
        clickFn: (dataFilterId: number) =>
          this.deleteFilter(dataFilterId, entity),
        itemType: VtFilterMenuItemType.Delete,
        disabled: !isEditableFilter,
      },
    ];
  }

  setActiveFilter(activeFilterId: number): void {
    const availableFilters = this.availableFilters$.getValue();

    this.availableFilters$.next(
      availableFilters.map(filter => ({
        ...filter,
        isActive: activeFilterId === filter.id,
      }))
    );
  }

  getOneFilter(
    dataFilterId: number,
    entity: Entity
  ): Observable<EntityFilterDto<T>> {
    return this.api
      .getData<EntityFilterDto<T>>({
        entity,
        fields: this.filterFields,
        filterModel: {
          DataFilterID: {
            filterType: 'nunber',
            type: 'equals',
            filter: dataFilterId,
          },
        },
      })
      .pipe(map(resp => resp.data[0]));
  }

  deselectActiveFilters(): void {
    const availableFilters = this.availableFilters$.getValue();
    if (availableFilters.some(filter => !!filter.isActive)) {
      this.availableFilters$.next(
        this.availableFilters$
          .getValue()
          .map(filter => ({ ...filter, isActive: false }))
      );
    }
  }

  openAddNewFilterDialog(
    entity: Entity,
    mappedPanelFormData?: Partial<EntityFilterDto<T>>
  ): void {
    this.filterCrudDialogService
      .openFilterCrudDialog({
        dataFilterAction: DataFilterAction.Add,
      })
      .closed.pipe(
        switchMap(event => {
          if (!event.responseData || event.source !== 'confirm') return of();
          const { responseData } = event;
          const payload: Partial<EntityFilterDto<T>> = {
            Name: responseData.Name,
            VisibleToAllUsers: responseData.VisibleToAllUsers,
            ...mappedPanelFormData,
          } as Partial<EntityFilterDto<T>>;
          return this.saveNewFilter(payload, undefined, entity);
        }),
        take(1)
      )
      .subscribe();
  }

  getSelectedFilterData(dataFilterId: number): EntityFilterDto<T> | undefined {
    const selectedFilterData = this.availableFilters$
      .getValue()
      .find(dataFilter => dataFilter.id === dataFilterId) as
      | EntityFilterDto<T>
      | undefined;

    return selectedFilterData;
  }

  getActiveFilter(): EntityFilterDto<T> | undefined {
    const availableFilters = this.availableFilters$.getValue();

    return availableFilters.find(filter => filter.isActive);
  }

  private mapFiltersForDvFilterArea(
    filters: Partial<EntityFilterDto<T>>[],
    entity: Entity
  ): EntityFilter<T>[] {
    return filters.map(
      dataFilter =>
        ({
          ...dataFilter,
          id: dataFilter.DataFilterID!,
          name: dataFilter.TranslationKey
            ? this.translate.instant(dataFilter.TranslationKey)
            : dataFilter.Name || dataFilter.Description || '',
          type: FilterAreaUtils.getFilterType(
            !!dataFilter.Editable,
            !!dataFilter.VisibleToAllUsers
          ),
          filterMenuItems: this.getFilterMenuItems(
            !!dataFilter.Editable,
            entity
          ),
        }) as EntityFilter<T>
    );
  }

  private updateFilter(entity: Entity): void {
    if (this.updateFilterDataGetterFn) {
      const payload = this.updateFilterDataGetterFn();
      let upsertPayload: EntityFilterDto<T> = {} as EntityFilterDto<T>;
      if (!payload) return;
      this.filterCrudDialogService
        .openFilterCrudDialog(
          {
            dataFilterAction: DataFilterAction.Update,
          },
          payload
        )
        .closed.pipe(
          switchMap(({ responseData, source }) => {
            if (!responseData || source !== 'confirm') return of();
            upsertPayload = {
              ...payload,
              Name: responseData.Name,
              FilterModel: payload.FilterModel,
              VisibleToAllUsers: responseData.VisibleToAllUsers,
            };

            delete upsertPayload.DataFilterID;

            return this.api
              .upsert(entity, payload.DataFilterID!, upsertPayload)
              .pipe(
                switchMap(
                  () =>
                    this.api
                      .getData<EntityFilterDto<T>>({
                        entity: entity,
                        fields: this.filterFields,
                      })
                      .pipe(map(resp => resp.data)) as Observable<
                      EntityFilter<T>[]
                    >
                )
              );
          }),
          take(1)
        )
        .subscribe(availableFilters => {
          this.availableFilters$.next(
            this.mapFiltersForDvFilterArea(availableFilters, entity)
          );
          this.setActiveFilter(payload.DataFilterID!);
        });
    }
  }

  private deleteFilter(activeFilterId: number, entity: Entity): void {
    this.api
      .delete(entity, activeFilterId)
      .pipe(take(1))
      .subscribe(_ => {
        const availableFilters = this.availableFilters$.getValue();
        const removeIndex = availableFilters.findIndex(
          onCallFilter => onCallFilter.id === activeFilterId
        );
        availableFilters.splice(removeIndex, 1);
        // spread ensures that change detection in pipe will work
        this.availableFilters$.next([...availableFilters]);
      });
  }

  private copyFilter(activeFilterId: number, entity: Entity): void {
    this.filterCrudDialogService
      .openFilterCrudDialog({ dataFilterAction: DataFilterAction.Copy })
      .closed.pipe(
        switchMap((event: DvDialogCloseEvent) => {
          if (event.source !== 'confirm') return of();

          const { responseData } = event;
          const filterCopyData = this.availableFilters$
            .getValue()
            .find(onCallFilter => onCallFilter.id === activeFilterId);

          if (!filterCopyData || !responseData) return of();

          return this.saveNewFilter(
            this.saveFilterDataGetterFn(responseData, filterCopyData),
            false,
            entity
          );
        }),
        take(1)
      )
      .subscribe();
  }

  private saveNewFilter(
    payload: Partial<EntityFilterDto<T>>,
    fetchNew = true,
    entity: Entity
  ): Observable<number | undefined> {
    return this.api.upsert(entity, '0', payload).pipe(
      switchMap(newFilterId =>
        fetchNew ? this.getOneFilter(newFilterId!, entity) : of(newFilterId)
      ),
      tap(dataFilter => {
        const availableFilters = this.availableFilters$.getValue();
        availableFilters.push(
          ...this.mapFiltersForDvFilterArea(
            [
              typeof dataFilter === 'number'
                ? { ...payload, DataFilterID: dataFilter }
                : (dataFilter as EntityFilterDto<T>),
            ],
            entity
          )
        );
        this.availableFilters$.next(availableFilters);
        this.deselectActiveFilters();
        this.setActiveFilter(
          typeof dataFilter === 'number'
            ? dataFilter
            : dataFilter!.DataFilterID!
        );
      }),
      map(dataFilter =>
        typeof dataFilter === 'number' ? dataFilter : dataFilter!.DataFilterID
      )
    );
  }
}
